import { useEffect, useState, type ImgHTMLAttributes } from 'react'
import { useTasks } from '../TaskProvider'
import { pictureBlob } from '../imageTransfer'
import type { BibleImage } from '../types'

export function BiblePicture({ picture, ...props }: { picture: BibleImage } & Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'>) {
  const { user } = useTasks()
  const [loaded, setLoaded] = useState<{ id: string; url: string } | null>(null)
  const [error, setError] = useState(false)
  useEffect(() => {
    if (picture.dataUrl) return
    let cancelled = false
    const load = () => { void pictureBlob(picture, user.id).then(blob => {
      if (cancelled) return
      const url = URL.createObjectURL(blob); setLoaded({ id: picture.assetId!, url }); setError(false)
    }).catch(() => { if (!cancelled) setError(true) }) }
    load(); window.addEventListener('online', load)
    return () => { cancelled = true; window.removeEventListener('online', load) }
  }, [picture.dataUrl, picture.assetId, user.id])
  // Keep a displayed URL alive until its replacement has rendered. Fast
  // carousel navigation can revisit an image before the next load finishes.
  useEffect(() => () => { if (loaded) URL.revokeObjectURL(loaded.url) }, [loaded])
  const src = picture.dataUrl || (loaded?.id === picture.assetId ? loaded?.url : undefined)
  if (!src && error) return <span role="status" className="empty-message">Reconnect to load {picture.name}.</span>
  if (!src) return <span role="status" aria-label="Loading picture">Loading picture…</span>
  return <img {...props} src={src} alt={props.alt ?? (picture.alt || picture.name)} />
}
